/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ChargeLine, Customer, ProductDeliveryOptions, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';
import { ICartState } from '@msdyn365-commerce/global-state';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    IPopupProps,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { getCartState } from '@msdyn365-commerce/global-state';
import axios from 'axios';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { vSI_RemoveAllChargesAsync } from '../../../../vsi-add-charges-extension/DataActionExtension.g';
import { GetUnbxdAddToCartProductInput } from '../../../../actions/get-unbxd-add-to-cart-product.action';
import { createDeliveryChargesUtil, IDeliveryChargeResult } from '../../utilities/delivery-charges.util';

export interface IDobbiesPopUpProps extends IPopupProps {
    cart?: ICartState;
    customerInformation?: Customer;
}

export interface IDobbiesPopUpProps extends IComponentProps<{ product: SimpleProduct | undefined; price: ProductPrice | undefined }> {
    dialogStrings?: {
        goToCartText: string;
        continueShoppingText: string;
        headerItemOneText: string;
        headerItemFormatText: string;
        headerMessageText: string;
        freePriceText: string;
        originalPriceText: string;
        currentPriceText: string;
    };
    cart?: ICartState;
    className: string;
    imageSettings?: IImageSettings;
    gridSettings: IGridSettings | undefined;
    productQuantity: number;
    priceComponent: React.ReactNode;
    navigationUrl?: string;
    modalOpen: boolean | undefined;
    telemetryContent?: ITelemetryContent;
    setModalOpen(newValue: boolean): void;
    unbxdCarousel: React.ReactNode[];
}

export const Popup = (popupProps: IDobbiesPopUpProps) => {
    const {
        dialogStrings,
        className,
        gridSettings,
        data: { product, price },
        productQuantity,
        priceComponent,
        navigationUrl,
        modalOpen,
        telemetryContent,
        setModalOpen,
        context
    } = popupProps;
    const closeModal = React.useCallback(() => {
        setModalOpen(false);
        document.body.classList.remove('buybox-open');
    }, [setModalOpen]);
    const OnOpenModal = React.useCallback(() => {
        document.body.classList.add('buybox-open');
    }, [setModalOpen]);

    if (!dialogStrings || !product) {
        return null;
    }
    const productURL = getProductUrlSync(product, popupProps.context.actionContext, undefined);

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.ContinueShopping);
    const continueShoppingAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.ViewBagAndCheckout;
    const viewBagAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    const formatCurrency = (price: number | undefined): string | undefined => {
        return context.cultureFormatter.formatCurrency(price || 0, undefined);
    };
    const fallbackImageUrl = getFallbackImageUrl(
        product.ItemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );

    const imageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=315&h=443&m=6', w: 315, h: 443 },
            lg: { q: 'w=315&h=443&m=6', w: 315, h: 443 },
            xl: { q: 'w=315&h=443&m=6', w: 315, h: 443 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    const [basketDeliveryCost, setBasketDeliveryCost] = React.useState(0);
    const [overlayCartState, setOverlayCartState] = React.useState<ICartState | undefined>();
    const [isCncOnlyCart, setIsCNCOnlyCart] = React.useState<boolean>();
    const [deliveryChargeResult, setDeliveryChargeResult] = React.useState<IDeliveryChargeResult | null>(null);

    // Initialize delivery charges utility
    const deliveryChargesUtil = React.useMemo(() => createDeliveryChargesUtil(context.actionContext), [context.actionContext]);

    const removeAllCharges = async (cartState: ICartState) => {
        try {
            if (cartState.cart.Id) {
                await vSI_RemoveAllChargesAsync({ callerContext: context.actionContext }, cartState.cart.Id);
            }
        } catch (e) {
            console.error('Error occured while removing charges', e);
        }
    };

    const addChargeToCart = async (cartState: ICartState, amount: number) => {
        const userToken = context.request.user.token;
        const isAuthenticated = context.request.user.isAuthenticated;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            OUN: context.request.apiSettings.oun
        };
        if (isAuthenticated) {
            headers['Authorization'] = `id_token ${userToken}`;
        }
        try {
            const res = await axios({
                method: 'POST',
                url: `${context.request.apiSettings.baseUrl}Commerce/VSIAddCharge/VSI_AddCharge`,
                data: {
                    cartId: cartState.cart.Id,
                    moduleTypeValue: 1,
                    chargeCode: 'Shipping',
                    amount: amount
                },
                headers: headers
            });
            await cartState.refreshCart({});
            context.request.cookies.set('default_customer_address_for_delivery_set', 'true');
            // let deliveryCost = 0;
            // res.data.ChargeLines?.forEach((chargeLine: { CalculatedAmount: number }) => {
            //     deliveryCost += chargeLine.CalculatedAmount || 0;
            // });
            res.data.ChargeLines?.reduce((acc: number, c: { CalculatedAmount: number }) => acc + (c.CalculatedAmount || 0), 0);
        } catch (error) {
            console.error('Adding charges error', error);
        }
    };

    const updateDeliveryCharges = async () => {
        const cartState = overlayCartState || (await getCartState(context.actionContext));
        if (!cartState) return;

        const deliveryOptions: ProductDeliveryOptions[] = await getDeliveryOptionsAsync(
            { callerContext: context.actionContext, queryResultSettings: {} },
            cartState.cart.CartLines?.map(cl => cl.ProductId).filter((id): id is number => id != null) || []
        );

        // Use utility to calculate delivery charges
        const chargeResult = deliveryChargesUtil.calculateDeliveryCharges(
            cartState,
            deliveryOptions,
            popupProps.context.request.channel?.PickupDeliveryModeCode
        );
        setDeliveryChargeResult(chargeResult);

        // Analyze cart for CNC status
        const cartAnalysis = deliveryChargesUtil.analyzeCart(cartState, popupProps.context.request.channel?.PickupDeliveryModeCode);
        setIsCNCOnlyCart(cartAnalysis.isCncOnlyCart);

        // Check if cart already has the correct charges
        const currentChargeAmount =
            cartState.cart.ChargeLines?.reduce((sum, charge) => {
                return sum + (charge.IsShipping ? charge.CalculatedAmount || 0 : 0);
            }, 0) || 0;

        const targetChargeAmount = chargeResult.chargeAmount;

        // Only update charges if they differ
        if (Math.abs(currentChargeAmount - targetChargeAmount) > 0.01) {
            await removeAllCharges(cartState);
            await addChargeToCart(cartState, targetChargeAmount);
        }

        // Update local delivery charges state with updated cart charges
        const updatedCartState = await getCartState(context.actionContext);
        const deliveryCharges = updatedCartState.cart.ChargeLines?.reduce((prevValue, charge: ChargeLine) => {
            return prevValue + (charge.CalculatedAmount || 0);
        }, 0);
        setBasketDeliveryCost(deliveryCharges || 0);
    };

    React.useEffect(() => {
        if (modalOpen) {
            getCartState(context.actionContext)
                .then(res => {
                    setOverlayCartState(res);
                    updateDeliveryCharges();
                })
                .catch(e => {
                    console.error(e);
                });
        } else {
            context.actionContext.update(new GetUnbxdAddToCartProductInput(), { product: null });
        }
    }, [modalOpen]);

    const quantity = productQuantity !== undefined ? productQuantity : 1;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    const shouldDisplayUnitOfMeasure =
        context.app.config?.unitOfMeasureDisplayType && context.app.config.unitOfMeasureDisplayType !== 'none';
    const reducedDimensions: string = product.Dimensions
        ? product.Dimensions.reduce<string>((str, productDimension) => {
              if (productDimension.DimensionValue && productDimension.DimensionValue.Value) {
                  if (str) {
                      return `${str}, ${productDimension.DimensionValue.Value}`;
                  }

                  return `${productDimension.DimensionValue.Value}`;
              }

              return str;
          }, '')
        : '';

    // Use utility for cart analysis
    const cartAnalysis = overlayCartState
        ? deliveryChargesUtil.analyzeCart(overlayCartState, popupProps.context.request.channel?.PickupDeliveryModeCode)
        : null;

    const homeDelQty = cartAnalysis?.homeDeliveryItemsCount || 0;
    const cncQty = cartAnalysis?.cncItemsCount || 0;
    const totalQuantity = cartAnalysis?.totalQuantity || 0;

    const totalAmount = overlayCartState?.cart?.TotalAmount;

    // Use delivery charge result from state (calculated in updateDeliveryCharges)
    const formattedDeliveryCharges =
        basketDeliveryCost !== undefined && popupProps?.context.cultureFormatter.formatCurrency(basketDeliveryCost);

    // Get spend more message if applicable
    const spendMoreMessage =
        deliveryChargeResult?.amountForFreeDelivery && deliveryChargeResult.amountForFreeDelivery > 0
            ? deliveryChargesUtil.getSpendMoreMessage(deliveryChargeResult.amountForFreeDelivery)
            : null;

    // Check if dominant mode is standard (qualifies for free delivery threshold)
    const isStandardDelivery = deliveryChargeResult?.dominantMode
        ? deliveryChargesUtil.isStandardMode(deliveryChargeResult.dominantMode)
        : false;

    // Determine if we should show spend more or free delivery message
    const showSpendMoreMessage = spendMoreMessage && !isCncOnlyCart && isStandardDelivery && !deliveryChargeResult?.isFreeDelivery;

    const showFreeDeliveryQualifiedMessage = deliveryChargeResult?.isFreeDelivery && isStandardDelivery && !isCncOnlyCart;

    // Use delivery message from the calculated result
    const deliveryMessage = deliveryChargeResult?.deliveryMessage || null;

    const showVariantKitConfig = context?.actionContext.requestContext?.app?.config?.showVariantsKitConfig || false;

    return (
        <Modal isOpen={modalOpen} onClosed={closeModal} onOpened={OnOpenModal} className={`${className}__dialog`} toggle={closeModal}>
            <ModalHeader toggle={closeModal} className={`${className}__dialog__header`}>
                <span>Added to basket</span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <div className={`${className}__dialog__body-upper`}>
                    <a href={productURL} className='popup-image'>
                        <Image
                            requestContext={context.actionContext.requestContext}
                            className={`${className}__dialog__image`}
                            src={product.PrimaryImageUrl || ''}
                            fallBackSrc={fallbackImageUrl}
                            altText={product.Name}
                            gridSettings={gridSettings || {}}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </a>
                    <div className={`${className}__dialog__product-details`}>
                        <a href={productURL} className={`${className}__dialog__product-title`}>
                            {product.Name}
                        </a>
                        {showVariantKitConfig && reducedDimensions !== '' ? (
                            <div className={`${className}__dialog__product-variants`}>{reducedDimensions}</div>
                        ) : (
                            ''
                        )}

                        <p className={`${className}__dialog__quantity`}>
                            Quantity: {quantity}
                            <span>{formatCurrency(price?.CustomerContextualPrice)} each</span>
                        </p>

                        {price ? <div className={`${className}__dialog__product-price`}>{priceComponent}</div> : ''}
                        {product.DefaultUnitOfMeasure && shouldDisplayUnitOfMeasure ? (
                            <div className={`${className}__dialog__product-unitOfMeasure`}>
                                <span>{product.DefaultUnitOfMeasure}</span>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
                <div className={`${className}__dialog__body-price-detail`}>
                    <div className='delivery-cost'>
                        {homeDelQty > 0 && (
                            <p>
                                <span>
                                    Basket delivery cost ({homeDelQty} {homeDelQty > 1 ? 'items' : 'item'})
                                </span>
                                {basketDeliveryCost === 0 ? 'Free' : formattedDeliveryCharges}
                            </p>
                        )}

                        {showSpendMoreMessage && <p className='delivery-cost-text'>{spendMoreMessage}</p>}

                        {showFreeDeliveryQualifiedMessage && (
                            <p className='delivery-cost-text'>{deliveryChargesUtil.getFreeDeliveryMessage()}</p>
                        )}

                        {deliveryMessage && <p className='delivery-cost-text'>{deliveryMessage}</p>}
                    </div>
                    {cncQty > 0 && (
                        <div className='cnc-items'>
                            <p>
                                <span>
                                    Click & Collect ({cncQty} {cncQty > 1 ? 'items' : 'item'})
                                </span>{' '}
                                Free
                            </p>
                            <p className='cnc-items-text'>Confirmed store pick-up and further information in basket.</p>
                        </div>
                    )}

                    <p className='basket-total'>
                        <span>
                            <b>Basket total</b> ({totalQuantity} {totalQuantity > 1 ? 'items' : 'item'})
                        </span>
                        {formatCurrency(totalAmount)}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter className={`${className}__dialog__footer`}>
                <Button
                    className={`${className}__dialog__go-to-cart`}
                    onClick={() => {
                        if (navigationUrl) {
                            window.location.href = navigationUrl;
                        }
                    }}
                    aria-label={context.actionContext.requestContext.user.isAuthenticated ? 'View basket' : 'View basket & checkout'}
                    {...viewBagAttribute}
                >
                    {context.actionContext.requestContext.user.isAuthenticated ? 'View basket' : 'View basket & checkout'}
                </Button>
                {context.actionContext.requestContext.user.isAuthenticated ? (
                    <Button
                        className={`${className}__dialog__checkout`}
                        onClick={() => {
                            const checkoutUrl = `${context.actionContext.requestContext.url.requestUrl.origin}/checkout`;
                            window.location.href = checkoutUrl;
                        }}
                        aria-label='Checkout'
                    >
                        Checkout
                    </Button>
                ) : (
                    ''
                )}

                <Button
                    className={`${className}__dialog__back-to-shopping ${
                        context.actionContext.requestContext.user.isAuthenticated ? 'sign-in' : 'not-sign-in'
                    }`}
                    onClick={closeModal}
                    aria-label={dialogStrings.continueShoppingText}
                    {...continueShoppingAttribute}
                >
                    {dialogStrings.continueShoppingText}
                </Button>

                {popupProps.unbxdCarousel && <div className='unbxd-recommendations pdp'>{popupProps.unbxdCarousel}</div>}
            </ModalFooter>
        </Modal>
    );
};

export default Popup;
